// src/pages/CollectionOrders/index.tsx
import React, { useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import useNavigationPage from '../../hooks/useNavigationPage';

import useCollectionOrderService from '../../app/services/hooks/useCollectionOrderService';
import { CollectionOrderDataForm, CollectionOrderFilters } from '../../app/models/CollectionOrder';
import CollectionOrderDataTable from './components/tables/CollectionOrderDataTable';
import CollectionOrderCreate from './components/CollectionOrderCreate';

const breadcrumbs: BreadcrumbsItem[] = [
  { name: 'Órdenes de Recolección', url: '/collection-orders', isActive: true }
];

const CollectionOrders = () => {
  const { navigationPage } = useNavigationPage();
  const { fetchingGetCollectionOrders, getCollectionOrders, deleteCollectionOrder } = useCollectionOrderService();

  const [items, setItems] = useState<CollectionOrderDataForm[]>([]);
  const [showingCreate, setShowingCreate] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);

  const [filters, setFilters] = useState<CollectionOrderFilters>({
    page: 1,
    per_page: 10,
    sort: 'id',
    order: 'desc',
    project: '',
    status: '',
    program_date_from: '',
    program_date_to: '',
    start_date_from: '',
    start_date_to: '',
  });

  useEffect(() => {
    reloadTable();
    // eslint-disable-next-line
  }, [filters.page, filters.per_page, filters.sort, filters.order]);

  const getAll = () => {
    getCollectionOrders(filters, {
      onSuccess: (response: ServiceResponse) => {
        setItems(response.data.collection_orders);
        setTotalRows(response.data.total_rows);
      },
      onError: (response: ServiceResponse) => {
        toast.error(response.message, { autoClose: 2000 });
        if (response.data?.to_dashboard) navigationPage('/');
      }
    });
  };

  const showEdit = (id: number) => { navigationPage(`/collection-order/${id}/edit`); };
  const showDetail = (id: number) => { navigationPage(`/collection-order/${id}/show`); };
  const showCreate = () => setShowingCreate(true);
  const hideCreate = () => setShowingCreate(false);
  const reloadTable = () => getAll();

  const destroy = (id: number) => {
    const _text = 'Está a punto de eliminar la orden de recolección #' + id;
    useReactConfirmAlert().requestConfirmation({
      title: '¿Estás seguro?',
      message: _text,
      buttons: {
        confirmButton: {
          label: 'Si, confirmar',
          onClick: () => {
            deleteCollectionOrder(id, {
              onSuccess: (response: ServiceResponse) => {
                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
                reloadTable();
              },
              onError: (response: ServiceResponse) => {
                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
              }
            });
          }
        },
        cancelButton: {
          label: 'No, cancelar',
          onClick: () => {
            setTimeout(() => {
              useReactConfirmAlert().infoAlert({ title: 'Cancelado', message: 'La orden no se ha eliminado.' });
            }, 0);
          }
        }
      }
    });
  };

  return (
    <>
      <Breadcrumbs pageSection="Órdenes de Recolección" breadcrumbs={breadcrumbs} />

      <DefaultCard>
        <DefaultToolBar left={<ButtonCreate callbackCreate={showCreate} title="Nueva orden" />} />
        <CollectionOrderDataTable
          items={items}
          edit={showEdit}
          show={showDetail}
          destroy={destroy}
          loading={fetchingGetCollectionOrders}
          filter={filters}
          setFilter={setFilters}
          totalRows={totalRows}
          callbackSuccess={reloadTable}
        />
      </DefaultCard>

      {showingCreate && (
        <DefaultModal
          show={showingCreate}
          handleClose={hideCreate}
          title="Crear orden de recolección"
          backdrop={true}
          showFooter={false}
        >
          <CollectionOrderCreate onSaved={() => { reloadTable(); hideCreate(); }} onCancel={hideCreate} />
        </DefaultModal>
      )}
    </>
  );
};

export default CollectionOrders;
